<template>
	<section class="home-widget" :class="{ 'number-group': children, 'number-card': widget.displayType === 'number' }" :style="{ minHeight: placement.height + 'px', '--metric-color': widget.color || 'var(--app-primary)' }">
		<div class="widget-heading">
			<span>{{ placement.title || widget.name }}</span>
			<el-button v-if="path" type="text" size="mini" @click="$router.push(path)">
				查看
				<i class="el-icon-arrow-right" />
			</el-button>
		</div>
		<div v-if="children" class="number-group-grid">
			<home-widget v-for="(child, index) in children" :key="child.keyId || index" :placement="{...child.placement, height: 120}" :widget="child.widget" :value="child.value" :error="child.error" :path="child.path" :loading="retrying === child.keyId" @retry="$emit('retry', child)" />
			<el-empty v-if="!children.length" :description="emptyGroupText" :image-size="45" />
		</div>
		<div v-else-if="error" class="widget-error">
			{{ error }}
			<el-button type="text" :loading="loading" @click="$emit('retry')">重试</el-button>
		</div>
		<div v-else-if="widget.displayType === 'number'" class="widget-number" :style="{ color: widget.color }">
			{{ formatted }}
			<small>{{ widget.unit }}</small>
		</div>
		<div v-else-if="widget.displayType === 'list'" key="list" class="widget-list" :style="{ maxHeight: Math.max(150, placement.height - 60) + 'px' }">
			<table v-if="table.rows.length">
				<thead><tr><th v-for="column in table.columns" :key="column.key" scope="col">{{ column.label }}</th></tr></thead>
				<tbody><tr v-for="(row, index) in table.rows" :key="index"><td v-for="column in table.columns" :key="column.key">{{ typeof row[column.key] === 'number' ? formatNumber(row[column.key]) : row[column.key] == null ? '—' : row[column.key] }}</td></tr></tbody>
			</table>
			<el-empty v-else description="暂无统计数据" :image-size="45" />
		</div>
		<el-empty v-else-if="!rows.length" description="暂无统计数据" :image-size="45" />
		<div v-else key="chart" ref="chart" :style="{ height: Math.max(150, placement.height - 60) + 'px' }" />
	</section>
</template>
<script>
import * as echarts from "echarts";
import { statisticChartOption, statisticTable } from "@/utils/homeLayout";
export default {
	name: "HomeWidget",
	props: { placement: Object, widget: Object, value: [Number, String, Array, Object], error: String, path: String, loading: Boolean, children: Array, retrying: String, emptyGroupText: { type: String, default: '暂无统计指标' } },
	computed: {
		table() { return statisticTable(this.value, this.widget); },
		rows() { return Array.isArray(this.value) ? this.value : []; },
		formatted() {
			return this.formatNumber(this.value);
		}
	},
	watch: {
		value: {
			handler() {
				this.$nextTick(this.draw);
			},
			deep: true
		},
		"widget.displayType"() {
			this.$nextTick(this.draw);
		},
		"widget.color"() {
			this.draw();
		},
		error() {
			this.$nextTick(this.draw);
		}
	},
	mounted() {
		this.draw();
		this.observer = new ResizeObserver(() => {
			if (this.chart) this.chart.resize();
		});
		this.observer.observe(this.$el);
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
		if (this.chart) this.chart.dispose();
	},
	methods: {
		formatNumber(value) {
			const n = value == null || value === '' ? NaN : Number(value), decimals = this.widget.decimals || 0;
			return Number.isFinite(n) ? n.toLocaleString('zh-CN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : '—';
		},
		draw() {
			if (!this.$refs.chart) {
				if (this.chart) {
					this.chart.dispose();
					this.chart = null;
				}
				return;
			}
			if (this.chart && this.chart.getDom() !== this.$refs.chart) {
				this.chart.dispose();
				this.chart = null;
			}
			if (!this.chart) this.chart = echarts.init(this.$refs.chart);
			this.chart.setOption(statisticChartOption(this.widget, this.rows), true);
		}
	}
};
</script>
<style scoped>
.widget-list { margin-top: 12px; overflow: auto; }
.widget-list table { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
.widget-list th, .widget-list td { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--app-border); }
.widget-list th { position: sticky; top: 0; background: var(--app-surface-muted); font-weight: 500; }
.widget-list th:last-child, .widget-list td:last-child { text-align: right; }
.widget-list tbody tr:nth-child(even) { background: var(--app-surface-muted); }
.number-group > .widget-heading { color: var(--app-text-primary); font-size: 16px; font-weight: 600; padding-bottom: 14px; margin-bottom: 16px; border-bottom: 1px solid var(--app-border); }
.number-group > .widget-heading > span { border-left: 3px solid var(--app-primary); padding-left: 10px; }
.number-group-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr)); gap: 12px; }
.number-group-grid .home-widget { background: var(--app-surface-muted); border: 0; border-top: 3px solid var(--metric-color); border-radius: 6px; }
.number-group-grid .widget-number { margin-top: 14px; font-variant-numeric: tabular-nums; }
.home-widget {
	background: var(--app-surface);
	border: 1px solid var(--app-border);
	border-radius: 8px;
	padding: 16px;
	box-sizing: border-box;
	overflow: hidden;
	height: 100%;
}
.widget-heading {
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: var(--app-text-secondary);
	gap: 8px;
	min-height: 24px;
}
.widget-number {
	font-size: 30px;
	font-weight: 600;
	margin-top: 20px;
	overflow-wrap: anywhere;
}
.widget-number small {
	font-size: 13px;
	font-weight: 400;
	margin-left: 8px;
}
.widget-error {
	padding-top: 20px;
	color: var(--app-text-secondary);
}
</style>
